// ── Input class strings ───────────────────────────────────────────────────────
// The single source of truth for text-control styling (Phase 3). Import these
// instead of declaring local INPUT_CLASSES/inputCls constants.
export const INPUT_CLS = [
  'tw-w-full tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-slate-300 tw-rounded tw-text-slate-800 tw-bg-white',
  'focus:tw-outline-none focus:tw-border-primary-500 focus:tw-ring-1 focus:tw-ring-primary-200 tw-transition-colors placeholder:tw-text-slate-400',
  'disabled:tw-bg-slate-50 disabled:tw-text-slate-400 disabled:tw-cursor-not-allowed',
].join(' ');

export const TEXTAREA_CLS = [
  'tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-300 tw-rounded tw-text-slate-800 tw-bg-white',
  'focus:tw-outline-none focus:tw-border-primary-500 focus:tw-ring-1 focus:tw-ring-primary-200 tw-transition-colors placeholder:tw-text-slate-400 tw-resize-y',
  'disabled:tw-bg-slate-50 disabled:tw-text-slate-400 disabled:tw-cursor-not-allowed',
].join(' ');

// Error state — append after INPUT_CLS/TEXTAREA_CLS when a field is invalid.
export const INPUT_ERROR_CLS =
  '!tw-border-red-400 focus:!tw-border-red-500 focus:!tw-ring-red-200';

// Label + error text (same look as pifStyles LABEL_CLS/ERROR_CLS).
export const LABEL_CLS = 'tw-text-sm tw-text-slate-600';
export const ERROR_CLS = 'tw-text-xs tw-text-red-500 tw-mt-1 tw-mb-0';

// ── Static data-table cells (non-AG Grid tables) ─────────────────────────────
// The house look for hand-built <table> markup — header + cell. Use with
// <table className="tw-w-full tw-border-collapse"> inside an overflow-x-auto
// wrapper. (Same values the PIF module standardized on.)
export const TH_CLS =
  'tw-bg-slate-50 tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-slate-600 tw-border tw-border-solid tw-border-slate-200';
export const TD_CLS =
  'tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-solid tw-border-slate-200 tw-align-top';

// ── react-select: borderless multi chip editor ───────────────────────────────
// Gmail-like recipient rows (no border on control, indigo chips). Was
// copy-pasted byte-identical in 20 enter_job files before Phase 3.
export const MULTI_CHIP_SELECT_STYLES = {
  control: (base) => ({ ...base, border: 'none', boxShadow: 'none', minHeight: 32, fontSize: 13 }),
  valueContainer: (base) => ({ ...base, padding: '2px 4px' }),
  multiValue: (base) => ({ ...base, backgroundColor: '#eef2ff', borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: '#4338ca', fontSize: 12, fontWeight: 500 }),
  multiValueRemove: (base) => ({ ...base, color: '#4338ca', '&:hover': { backgroundColor: '#c7d2fe', color: '#4338ca' } }),
  indicatorsContainer: (base) => ({ ...base, alignSelf: 'center' }),
  menuPortal: (base) => ({ ...base, zIndex: 100001 }),
};

// ── react-select matching INPUT_CLS ───────────────────────────────────────────
// For forms that mix plain inputs and selects: same 14px text, height, border,
// radius and focus ring as INPUT_CLS, so the two read as one control family.
export const INPUT_SELECT_STYLES = {
  control: (b, s) => ({
    ...b,
    minHeight: 34,
    fontSize: 14,
    borderRadius: 4,
    borderColor: s.isFocused ? '#3b82f6' : '#cbd5e1',
    boxShadow: s.isFocused ? '0 0 0 1px #bfdbfe' : 'none',
    backgroundColor: s.isDisabled ? '#f8fafc' : '#fff',
    '&:hover': { borderColor: s.isFocused ? '#3b82f6' : '#94a3b8' },
  }),
  valueContainer:     (b)    => ({ ...b, padding: '0 12px' }),
  singleValue:        (b, s) => ({ ...b, fontSize: 14, color: s.isDisabled ? '#64748b' : '#1e293b' }),
  placeholder:        (b)    => ({ ...b, fontSize: 14, color: '#94a3b8' }),
  input:              (b)    => ({ ...b, fontSize: 14, margin: 0, padding: 0 }),
  option:             (b)    => ({ ...b, fontSize: 14 }),
  indicatorSeparator: (b)    => ({ ...b, marginTop: 6, marginBottom: 6 }),
  dropdownIndicator:  (b)    => ({ ...b, padding: '0 8px' }),
  menu:               (b)    => ({ ...b, zIndex: 9999, borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0' }),
};

// ── react-select compact styles ───────────────────────────────────────────────
export const SELECT_STYLES = {
  control:     (b, s) => ({ ...b, fontSize: 13, minHeight: 36, borderRadius: 8, borderColor: s.isFocused ? '#60a5fa' : '#e2e8f0', boxShadow: s.isFocused ? '0 0 0 3px rgba(96,165,250,0.2)' : 'none' }),
  option:      (b)    => ({ ...b, fontSize: 13 }),
  singleValue: (b)    => ({ ...b, fontSize: 13 }),
  multiValue:  (b)    => ({ ...b, fontSize: 12 }),
  placeholder: (b)    => ({ ...b, fontSize: 13, color: '#94a3b8' }),
  input:       (b)    => ({ ...b, fontSize: 13 }),
  menu:        (b)    => ({ ...b, zIndex: 9999, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0' }),
};
